﻿const GENKI_MIN_CUSTOM_QUERY_LENGTH = 3;
const GENKI_MAX_CUSTOM_RESULTS = 80;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.indexPromise = null;
    this.quickStatus = 'all';
    this.pointerInside = false;
    this.keepOpenUntil = 0;
    this.resultsTouchY = 0;
    this.visibleCustomLimit = GENKI_MAX_CUSTOM_RESULTS;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onQuickFilterClick.bind(this));
    this.addEventListener('pointerdown', this.onPointerDownInside.bind(this));
    this.predictiveSearchResults.addEventListener('wheel', this.onResultsWheel.bind(this), { passive: false, capture: true });
    this.predictiveSearchResults.addEventListener('touchstart', this.onResultsTouchStart.bind(this), { passive: true, capture: true });
    this.predictiveSearchResults.addEventListener('touchmove', this.onResultsTouchMove.bind(this), { passive: false, capture: true });
    document.addEventListener('pointerup', this.onPointerUp.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (newSearchTerm !== this.searchTerm) {
      this.visibleCustomLimit = GENKI_MAX_CUSTOM_RESULTS;
    }
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }
    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (Date.now() < this.keepOpenUntil) {
        this.open();
        return;
      }
      if (this.pointerInside) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onPointerDownInside() {
    this.pointerInside = true;
    this.keepOpenUntil = Date.now() + 700;
  }

  onPointerUp() {
    setTimeout(() => {
      this.pointerInside = false;
    });
  }

  scrollResultsBy(deltaY) {
    const scroller = this.predictiveSearchResults;
    if (!scroller) return false;
    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    if (!maxScroll) return false;
    const nextTop = Math.max(0, Math.min(maxScroll, scroller.scrollTop + deltaY));
    if (nextTop === scroller.scrollTop) return false;
    scroller.scrollTop = nextTop;
    return true;
  }

  onResultsWheel(event) {
    if (!this.isOpen) return;
    if (this.scrollResultsBy(event.deltaY)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onResultsTouchStart(event) {
    this.resultsTouchY = event.touches?.[0]?.clientY || 0;
  }

  onResultsTouchMove(event) {
    if (!this.isOpen) return;
    const currentY = event.touches?.[0]?.clientY || 0;
    const deltaY = this.resultsTouchY - currentY;
    this.resultsTouchY = currentY;
    if (this.scrollResultsBy(deltaY)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onQuickFilterClick(event) {
    const resultLink = event.target.closest('[data-genki-search-click="true"]');
    if (resultLink) {
      window.GenkiAnalytics?.searchClick?.(
        this.searchTerm,
        resultLink.dataset.genkiProductTitle || '',
        resultLink.getAttribute('href') || '',
        resultLink.dataset.genkiProductStatus || ''
      );
      return;
    }

    const suggestion = event.target.closest('[data-predictive-suggestion]');
    if (suggestion) {
      event.preventDefault();
      event.stopPropagation();
      const suggestedTerm = suggestion.dataset.predictiveSuggestion || '';
      if (!suggestedTerm) return;
      window.GenkiAnalytics?.searchSuggestion?.(this.searchTerm, suggestedTerm);
      this.keepOpenUntil = Date.now() + 900;
      this.input.value = suggestedTerm;
      this.searchTerm = suggestedTerm;
      this.quickStatus = 'all';
      this.getCustomSearchResults(suggestedTerm, this.cacheKey(suggestedTerm));
      this.open();
      requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
      return;
    }

    const loadMore = event.target.closest('[data-predictive-load-more]');
    if (loadMore) {
      event.preventDefault();
      event.stopPropagation();
      this.keepOpenUntil = Date.now() + 900;
      this.visibleCustomLimit = (this.visibleCustomLimit || GENKI_MAX_CUSTOM_RESULTS) + GENKI_MAX_CUSTOM_RESULTS;
      if (this.searchTerm) {
        this.getCustomSearchResults(this.searchTerm, this.cacheKey(this.searchTerm));
        this.open();
        requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
      }
      return;
    }

    const button = event.target.closest('[data-predictive-status]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    this.keepOpenUntil = Date.now() + 900;
    this.quickStatus = button.dataset.predictiveStatus || 'all';
    window.GenkiAnalytics?.searchStatus?.(this.searchTerm, this.quickStatus);
    if (this.searchTerm) {
      this.getCustomSearchResults(this.searchTerm, this.cacheKey(this.searchTerm));
      this.open();
      requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    }
  }

  cacheKey(searchTerm) {
    return `${searchTerm.replace(' ', '-').toLowerCase()}-${this.quickStatus || 'all'}`;
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = (this.dataset.liveSearchUrl || this.dataset.indexUrl) ? this.cacheKey(searchTerm) : searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.dataset.liveSearchUrl) {
      this.getLiveSearchResults(searchTerm, queryKey);
      return;
    }

    if (this.dataset.indexUrl) {
      this.getCustomSearchResults(searchTerm, queryKey);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  getLiveSearchResults(searchTerm, queryKey) {
    const url = new URL(this.dataset.liveSearchUrl, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('limit', '30');

    fetch(url.toString(), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Live search failed: ${response.status}`);
        return response.json();
      })
      .then((data) => {
    const products = Array.isArray(data.products)
      ? data.products.filter((product) => product?.handle !== 'extra-versand')
      : [];
        const resultsMarkup = this.buildCustomResults(searchTerm, products);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') return;
        if (this.dataset.indexUrl) {
          this.getCustomSearchResults(searchTerm, queryKey);
          return;
        }
        this.getSearchResults(searchTerm);
      });
  }

  getIndexProducts() {
    if (!this.indexPromise) {
      this.indexPromise = fetch(this.dataset.indexUrl, { credentials: 'same-origin' })
        .then(async (response) => {
          if (!response.ok) throw new Error(`Index request failed: ${response.status}`);
          const data = await response.json();
          if (data.chunked === true) {
            if (!Array.isArray(data.chunks) || !data.chunks.length) throw new Error('Index chunks missing');
            data.products = [];
            for (const chunkName of data.chunks) {
              const chunkResponse = await fetch(new URL(chunkName, this.dataset.indexUrl).toString(), { credentials: 'same-origin' });
              if (!chunkResponse.ok) throw new Error(`Index chunk request failed: ${chunkResponse.status}`);
              const chunk = await chunkResponse.json();
              if (chunk.generated_at !== data.generated_at || !Array.isArray(chunk.products)) throw new Error('Index chunks mismatch');
              data.products.push(...chunk.products);
            }
          }
          if (!Array.isArray(data.products) || data.products.length !== data.product_count) throw new Error('Index product count mismatch');
          return data;
        })
        .then((data) => (data.products || []).filter((product) => product?.handle !== 'extra-versand'));
    }
    return this.indexPromise;
  }

  getCustomSearchResults(searchTerm, queryKey) {
    const normalizedLength = String(searchTerm || '').trim().replace(/\s+/g, '').length;
    if (normalizedLength < GENKI_MIN_CUSTOM_QUERY_LENGTH) {
      this.renderSearchResults(this.buildTooShortResults(searchTerm));
      return;
    }

    this.getIndexProducts()
      .then((products) => {
        const resultsMarkup = this.buildCustomResults(searchTerm, products);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch(() => {
        this.dataset.indexUrl = '';
        this.getSearchResults(searchTerm);
      });
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  publicSearchValue(value) {
    return String(value || '').replace(/\bsec;;.*$/i, '').trim();
  }

  imageUrl(src, width) {
    if (!src) return '';
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  timeValue(value) {
    const parsed = Date.parse(value || '');
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  productStatus(product) {
    if (product.preorder) return { key: 'preorder', label: 'Vorbesteller' };
    if (product.available) return { key: 'available', label: 'Verfuegbar' };
    return { key: 'soldout', label: 'Ausverkauft' };
  }

  normalizedReleaseDate(product) {
    const raw = String(product.release_date || '').trim();
    const sort = String(product.release_sort || '').trim();
    const rawMatch = raw.match(/(?:^|\D)(0?[1-9]|1[0-2])[\s./-]+(20\d{2})(?:\D|$)/);
    if (rawMatch) return `${rawMatch[1].padStart(2, '0')}.${rawMatch[2]}`;
    const sortMatch = sort.match(/^(20\d{2})-(0[1-9]|1[0-2])$/);
    if (sortMatch) return `${sortMatch[2]}.${sortMatch[1]}`;
    return '';
  }

  isFutureRelease(product) {
    if (!product.preorder || !this.normalizedReleaseDate(product) || !product.release_sort) return false;
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return String(product.release_sort) >= currentMonth;
  }

  priceMarkup(product) {
    const price = Number(product.price || 0);
    const compareAt = Number(product.compare_at || 0);
    if (price <= 0) return '';
    const formatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    const current = `${product.price_varies ? 'ab ' : ''}${formatter.format(price)}`;
    if (compareAt > price) {
      return `
        <div class="predictive-search__price">
          <span class="predictive-search__compare">${formatter.format(compareAt)}</span>
          <strong class="predictive-search__sale-price">${current}</strong>
        </div>
      `;
    }
    return `<div class="predictive-search__price"><strong>${current}</strong></div>`;
  }

  releaseMarkup(product) {
    if (!this.isFutureRelease(product)) return '';
    const releaseDate = this.normalizedReleaseDate(product);
    return `
      <span class="predictive-search__release">
        ${product.release_changed ? 'Neuer Termin' : 'Release'} <strong>${this.escapeHtml(releaseDate)}</strong>
      </span>
    `;
  }

  productMatchesSearch(product, terms) {
    const variantText = Array.isArray(product.variants)
      ? product.variants.flatMap((variant) => [variant.title, variant.sku, this.publicSearchValue(variant.barcode), variant.option1, variant.option2, variant.option3])
      : [];
    const haystack = [
      product.title,
      product.ean,
      product.vendor,
      product.origin,
      ...(Array.isArray(product.type) ? product.type : [product.type]),
      ...(Array.isArray(product.size) ? product.size : [product.size]),
      ...(Array.isArray(product.scale) ? product.scale : [product.scale]),
      ...(Array.isArray(product.platform) ? product.platform : [product.platform]),
      ...(Array.isArray(product.search_values) ? product.search_values : []),
      ...variantText,
    ].join(' ').toLowerCase();
    return terms.every((term) => haystack.includes(term));
  }

  productSearchText(product) {
    const variantText = Array.isArray(product.variants)
      ? product.variants.flatMap((variant) => [variant.title, variant.sku, this.publicSearchValue(variant.barcode), variant.option1, variant.option2, variant.option3])
      : [];
    return [
      product.title,
      product.ean,
      product.vendor,
      product.origin,
      ...(Array.isArray(product.type) ? product.type : [product.type]),
      ...(Array.isArray(product.platform) ? product.platform : [product.platform]),
      ...(Array.isArray(product.search_values) ? product.search_values : []),
      ...variantText,
    ].join(' ').toLowerCase();
  }

  wordDistance(left, right) {
    const a = String(left || '');
    const b = String(right || '');
    if (!a || !b) return 99;
    const matrix = Array.from({ length: a.length + 1 }, (_, index) => [index]);
    for (let column = 1; column <= b.length; column += 1) matrix[0][column] = column;
    for (let row = 1; row <= a.length; row += 1) {
      for (let column = 1; column <= b.length; column += 1) {
        const cost = a[row - 1] === b[column - 1] ? 0 : 1;
        matrix[row][column] = Math.min(
          matrix[row - 1][column] + 1,
          matrix[row][column - 1] + 1,
          matrix[row - 1][column - 1] + cost
        );
      }
    }
    return matrix[a.length][b.length];
  }

  suggestionForTerms(terms, products) {
    if (!terms.length) return '';
    const words = new Map();
    products.slice(0, 5000).forEach((product) => {
      this.productSearchText(product).match(/[a-z0-9Ã¤Ã¶Ã¼ÃŸ]{4,}/gi)?.forEach((word) => {
        const normalized = word.toLowerCase();
        words.set(normalized, (words.get(normalized) || 0) + 1);
      });
    });

    const corrected = terms.map((term) => {
      if (term.length < 4 || words.has(term)) return term;
      let bestWord = term;
      let bestDistance = Math.min(3, Math.ceil(term.length * 0.35));
      let bestCount = 0;
      words.forEach((count, word) => {
        if (Math.abs(word.length - term.length) > 4) return;
        const distance = this.wordDistance(term, word);
        if (distance < bestDistance || (distance === bestDistance && count > bestCount)) {
          bestWord = word;
          bestDistance = distance;
          bestCount = count;
        }
      });
      return bestWord;
    });

    const suggestion = corrected.join(' ');
    return suggestion !== terms.join(' ') ? suggestion : '';
  }

  suggestionMarkup(suggestion) {
    if (!suggestion) return '';
    const pretty = suggestion.replace(/\b\w/g, (letter) => letter.toUpperCase());
    return `
      <li class="predictive-search__suggestion">
        <button class="predictive-search__suggestion-button" type="button" data-predictive-suggestion="${this.escapeHtml(suggestion)}">
          Meintest du vielleicht <strong>${this.escapeHtml(pretty)}</strong>?
        </button>
      </li>
    `;
  }

  matchingVariant(product, terms) {
    if (!Array.isArray(product.variants) || !product.variants.length) return null;
    let bestVariant = null;
    let bestScore = 0;
    product.variants.forEach((variant) => {
      const title = String(variant.title || '').toLowerCase();
      const score = terms.reduce((count, term) => count + (title.includes(term) ? 1 : 0), 0);
      if (score > bestScore) {
        bestScore = score;
        bestVariant = variant;
      }
    });
    return bestScore > 0 ? bestVariant : null;
  }

  isCustomerVariant(variant) {
    const title = String(variant?.title || '').trim();
    if (!title || title.toLowerCase() === 'default title') return false;
    if (/^(genkidama|eu)\s*\d+/i.test(title)) return false;
    return true;
  }

  isGameProduct(product) {
    const platformValues = Array.isArray(product.platform) ? product.platform : [product.platform];
    if (platformValues.some((value) => String(value || '').trim())) return true;
    const text = [
      product.origin,
      ...(Array.isArray(product.type) ? product.type : [product.type]),
      product.title,
    ].join(' ').toLowerCase();
    return /\b(game|games|videospiel|videospiele|playstation|ps4|ps5|xbox|switch|nintendo|pc)\b/.test(text);
  }

  productMatchesOnly(product, terms) {
    const haystack = [
      product.title,
      product.ean,
      product.vendor,
      product.origin,
      ...(Array.isArray(product.type) ? product.type : [product.type]),
      ...(Array.isArray(product.size) ? product.size : [product.size]),
      ...(Array.isArray(product.scale) ? product.scale : [product.scale]),
      ...(Array.isArray(product.platform) ? product.platform : [product.platform]),
      ...(Array.isArray(product.search_values) ? product.search_values : []),
    ].join(' ').toLowerCase();
    return terms.every((term) => haystack.includes(term));
  }

  searchEntriesForProduct(product, terms) {
    if (!this.isGameProduct(product)) {
      return [{ product, variant: null, displayProduct: product }];
    }

    const variants = Array.isArray(product.variants) ? product.variants.filter((variant) => this.isCustomerVariant(variant)) : [];
    if (!variants.length) {
      const displayProduct = this.productForVariant(product, this.matchingVariant(product, terms));
      return [{ product, variant: null, displayProduct }];
    }

    const productMatch = this.productMatchesOnly(product, terms);
    const variantMatches = variants.filter((variant) => {
      const haystack = [product.ean, variant.title, variant.sku, this.publicSearchValue(variant.barcode)].join(' ').toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
    const shownVariants = productMatch ? variants : variantMatches;

    if (!shownVariants.length) {
      const displayProduct = this.productForVariant(product, this.matchingVariant(product, terms));
      return [{ product, variant: null, displayProduct }];
    }

    return shownVariants.map((variant) => ({
      product,
      variant,
      displayProduct: this.productForVariant(product, variant),
    }));
  }

  productForVariant(product, variant) {
    if (!variant) return product;
    return {
      ...product,
      price: Number(variant.price || 0) > 0 ? variant.price : product.price,
      compare_at: Number(variant.compare_at || 0) > 0 ? variant.compare_at : product.compare_at,
      price_varies: false,
      available: Boolean(variant.available),
      preorder: Boolean(variant.preorder),
      release_date: variant.release_date || product.release_date,
      release_changed: Boolean(variant.release_changed || product.release_changed),
      release_sort: variant.release_sort || product.release_sort,
    };
  }

  productStatusKey(product) {
    return this.productStatus(product).key;
  }

  buildTooShortResults(searchTerm) {
    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--with-quick-filters">
          <aside class="predictive-search__quick-panel" aria-label="Suchfilter">
            <h2 class="predictive-search__quick-title">Status</h2>
            <div class="predictive-search__quick-list">
              <button class="predictive-search__quick-filter predictive-search__quick-filter--all is-active" type="button" disabled>
                <span>Bitte weiter tippen</span>
                <strong>${GENKI_MIN_CUSTOM_QUERY_LENGTH}+</strong>
              </button>
            </div>
          </aside>
          <div class="predictive-search__result-group">
            <div>
              <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Produkte</h2>
              <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                <li class="predictive-search__list-item"><div class="predictive-search__item">Tippe mindestens ${GENKI_MIN_CUSTOM_QUERY_LENGTH} Zeichen, damit die Suche startet.</div></li>
              </ul>
            </div>
          </div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>Bitte weiter tippen</span>
      </div>
    `;
  }
  quickFilterMarkup(matches) {
    const counts = matches.reduce(
      (totals, entry) => {
        const displayProduct = entry.displayProduct || entry.product || entry;
        totals.all += 1;
        totals[this.productStatusKey(displayProduct)] += 1;
        return totals;
      },
      { all: 0, preorder: 0, available: 0, soldout: 0 }
    );
    const filters = [
      ['all', 'Alle', counts.all],
      ['preorder', 'Vorbesteller', counts.preorder],
      ['available', 'Verfuegbar', counts.available],
      ['soldout', 'Ausverkauft', counts.soldout],
    ];
    return `
      <aside class="predictive-search__quick-panel" aria-label="Suchfilter">
        <h2 class="predictive-search__quick-title">Status</h2>
        <div class="predictive-search__quick-list">
          ${filters.map(([key, label, count]) => `
            <button
              class="predictive-search__quick-filter predictive-search__quick-filter--${key}${this.quickStatus === key ? ' is-active' : ''}"
              type="button"
              data-predictive-status="${key}"
              ${count ? '' : 'disabled'}
            >
              <span>${label}</span>
              <strong>${count}</strong>
            </button>
          `).join('')}
        </div>
      </aside>
    `;
  }

  buildCustomResults(searchTerm, products) {
    const terms = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    this.searchTerms = terms;
    const allMatches = products
      .filter((product) => product.handle && product.handle !== 'extra-versand' && this.productMatchesSearch(product, terms))
      .sort((a, b) => this.timeValue(b.created_at) - this.timeValue(a.created_at) || Number(b.id || 0) - Number(a.id || 0));
    const suggestion = allMatches.length ? '' : this.suggestionForTerms(terms, products);
    const allEntries = allMatches.flatMap((product) => this.searchEntriesForProduct(product, terms));
    const statusMatches = this.quickStatus === 'all'
      ? allEntries
      : allEntries.filter((entry) => this.productStatusKey(entry.displayProduct) === this.quickStatus);
    const trackingKey = `${searchTerm}|${this.quickStatus || 'all'}|${statusMatches.length}`;
    if (this.lastTrackedSearchKey !== trackingKey) {
      this.lastTrackedSearchKey = trackingKey;
      window.GenkiAnalytics?.search?.(searchTerm, statusMatches.length, this.quickStatus || 'all');
      if (!allMatches.length) window.GenkiAnalytics?.searchNoResults?.(searchTerm);
    }
    const limit = Math.max(GENKI_MAX_CUSTOM_RESULTS, this.visibleCustomLimit || GENKI_MAX_CUSTOM_RESULTS);
    const matches = statusMatches.slice(0, limit);
    const hasMore = statusMatches.length > matches.length;

    const items = matches.map((entry, index) => {
      const product = entry.product;
      const matchedVariant = entry.variant;
      const displayProduct = entry.displayProduct;
      const status = this.productStatus(displayProduct);
      const productUrl = matchedVariant?.id ? `${product.url}?variant=${matchedVariant.id}` : product.url;
      const trackingTitle = matchedVariant ? `${product.title} - ${matchedVariant.title}` : product.title;
      const image = product.image
        ? `<img class="predictive-search__image" src="${this.escapeHtml(this.imageUrl(product.image, 360))}" srcset="${this.escapeHtml(this.imageUrl(product.image, 360))} 1x, ${this.escapeHtml(this.imageUrl(product.image, 720))} 2x" alt="${this.escapeHtml(product.title)}" width="72" height="72" loading="lazy" decoding="async">`
        : '';
      return `
        <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item predictive-search__list-item--${status.key}" role="option" aria-selected="false">
          <a href="${this.escapeHtml(productUrl)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1" data-genki-search-click="true" data-genki-product-title="${this.escapeHtml(trackingTitle)}" data-genki-product-status="${this.escapeHtml(status.key)}">
            ${image}
            <div class="predictive-search__item-content">
              <div class="predictive-search__status-row">
                <span class="predictive-search__status predictive-search__status--${status.key}">${status.label}</span>
                ${this.releaseMarkup(displayProduct)}
              </div>
              ${product.vendor && product.vendor !== '-' ? `<div class="predictive-search__item-vendor caption-with-letter-spacing">${this.escapeHtml(product.vendor)}</div>` : ''}
              <p class="predictive-search__item-heading h5">${this.escapeHtml(product.title)}</p>
              ${matchedVariant ? `<div class="predictive-search__variant">Variante: <strong>${this.escapeHtml(matchedVariant.title)}</strong></div>` : ''}
              ${this.priceMarkup(displayProduct)}
            </div>
          </a>
        </li>
      `;
    }).join('');

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--with-quick-filters">
          ${this.quickFilterMarkup(allEntries)}
          <div class="predictive-search__result-group">
            <div>
              <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Produkte</h2>
              <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                ${items || this.suggestionMarkup(suggestion) || '<li class="predictive-search__list-item"><div class="predictive-search__item">Keine Produkte gefunden.</div></li>'}
              </ul>
              ${hasMore ? `
                <button class="predictive-search__load-more" type="button" data-predictive-load-more>
                  Mehr laden <span>${matches.length} / ${statusMatches.length}</span>
                </button>
              ` : ''}
            </div>
          </div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>${statusMatches.length} Produkte gefunden, ${matches.length} angezeigt</span>
      </div>
    `;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const inputRect = this.input.getBoundingClientRect();
    const panelTop = inputRect.bottom + 1;
    const panelGap = isMobile ? 10 : 16;
    const availableHeight = Math.max(isMobile ? 320 : 360, window.innerHeight - panelTop - panelGap);
    const panelLeft = isMobile ? 12 : inputRect.left;
    const panelWidth = isMobile ? Math.max(280, window.innerWidth - 24) : inputRect.width;

    Object.assign(this.predictiveSearchResults.style, {
      position: 'fixed',
      top: `${panelTop}px`,
      left: `${panelLeft}px`,
      right: 'auto',
      width: `${panelWidth}px`,
      height: `${availableHeight}px`,
      maxHeight: `${availableHeight}px`,
      display: 'block',
      overflowY: 'auto',
      overflowX: 'hidden',
      overscrollBehavior: 'contain',
      webkitOverflowScrolling: 'touch',
      touchAction: 'pan-y',
      zIndex: '9999',
    });

    const groups = this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper, .predictive-search__results-groups-wrapper');
    if (groups) {
      Object.assign(groups.style, {
        flex: '1 1 auto',
        minHeight: '0',
        height: 'auto',
        overflow: 'visible',
      });
    }

    const productsList = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (productsList) {
      Object.assign(productsList.style, {
        flex: '1 1 auto',
        minHeight: '0',
        maxHeight: 'none',
        overflowY: 'visible',
        overflowX: 'visible',
        paddingBottom: isMobile ? '8rem' : '4rem',
        overscrollBehavior: 'contain',
        webkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
      });
    }

    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);


